import { useEffect, useState } from 'react';
import { Check, Info, AlertTriangle, X } from 'lucide-react';

/**
 * Hand-rolled toast system. Zero deps. Brand-themed via existing CSS tokens.
 *
 * Why not sonner / shadcn toast? We don't need their flexibility for v1, and
 * the sandbox has no npm reach. Keeping this in-house keeps the brand voice
 * tight and means there's no version pinning to babysit. If we ever need
 * stacking-with-collapse, swipe-to-dismiss, etc., revisit the dep choice.
 *
 * Usage:
 *   import { notify } from '@/lib/notify';
 *   notify.success('Saved');
 *   notify.error('Something went wrong');
 *
 * Mount once near the root of the tree (App.tsx). The Toaster reads from a
 * tiny module-level event bus exposed by lib/notify.ts so any code can call
 * notify.* without React context plumbing.
 */

import {
  subscribeToToasts,
  type Toast,
  dismissToastById,
} from '@/lib/notify';

export function Toaster() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    return subscribeToToasts(setToasts);
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div
      // aria-live "polite" — announce success/info passively without
      // interrupting screen-reader users.
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col-reverse gap-2 print:hidden"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  );
}

function ToastItem({ toast }: { toast: Toast }) {
  const icon = {
    success: <Check className="h-4 w-4 text-emerald-600" aria-hidden="true" />,
    error: <AlertTriangle className="h-4 w-4 text-red-600" aria-hidden="true" />,
    info: <Info className="h-4 w-4 text-blue-600" aria-hidden="true" />,
  }[toast.kind];

  const accent = {
    success: 'border-emerald-200 bg-emerald-50',
    error: 'border-red-200 bg-red-50',
    info: 'border-blue-200 bg-blue-50',
  }[toast.kind];

  return (
    <div
      role={toast.kind === 'error' ? 'alert' : 'status'}
      className={`pointer-events-auto flex items-start gap-3 rounded-md border ${accent} px-3 py-2 text-sm shadow-md min-w-[16rem] max-w-md`}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="flex-1 leading-snug text-foreground">
        {toast.title && (
          <div className="font-medium text-foreground">{toast.title}</div>
        )}
        <div className="text-foreground/90">{toast.message}</div>
      </div>
      <button
        type="button"
        onClick={() => dismissToastById(toast.id)}
        aria-label="Dismiss notification"
        className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}
